<template>
  <div
    class="listening-assessment base-form"
    @copy="event_cancellation"
    @paste="event_cancellation"
    @cut="event_cancellation"
  >
    <h2 class="mt-2">
      {{
        currentPartGroup?.questions[0]?.question_options?.statement_title || ""
      }}
    </h2>

    <!-- Display only current part -->
    <div v-if="currentPartGroup" class="part-section">
      <h3 class="my-3 part-title">
        {{ currentPartGroup.questions[0]?.question_options?.head_title }}
      </h3>

      <!-- Progress indicator -->
      <div class="progress-indicator">
        Part {{ currentPart }} of {{ totalParts }}
      </div>

      <!-- Audio Player for this part -->
      <div class="audio-box" v-if="currentPartGroup.audioUrl">
        <CustomAudioPlayer :src="currentPartGroup.audioUrl" />
      </div>

      <div class="questions-box" v-if="currentAssessmentQuestions.length > 0">
        <h3>Questions</h3>
        <div
          class="question-item form-field"
          v-for="(question, index) in currentAssessmentQuestions"
          :key="index"
        >
          <label
            class="form-field-label"
            @copy="event_cancellation"
            @paste="event_cancellation"
            @cut="event_cancellation"
            >{{ index + 1 }}: {{ question.question_text }}
            <span class="required">*</span></label
          >
          <div class="form-field-input">
            <v-textarea
              :value="answers[index]?.question_answer || ''"
              auto-grow
              outlined
              :error="$v.answers.$each[index]?.$error"
              :error-messages="answerErrors(index)"
              placeholder="Type your answer here..."
              @input="onAnswerInput($event, index)"
              @paste="event_cancellation"
              @copy="event_cancellation"
              @cut="event_cancellation"
            ></v-textarea>
          </div>
        </div>
      </div>

      <v-row>
        <v-col cols="12">
          <div class="text-end">
            <v-btn
              :loading="submitLoading"
              :disabled="disabled_next_question"
              style="max-width: 150px"
              @click="submitAnswers"
              color="primary"
              class="next-btn base-submit-button"
            >
              {{ nextButtonLabel }}
            </v-btn>
          </div>
        </v-col>
      </v-row>
      <p v-if="submitError" class="error-text">{{ submitError }}</p>
    </div>
  </div>
</template>

<script>
import { AppAssessments } from "@/interfaces/data_objects/app_assessments";
import { SUBMIT_APP_ASSESSMENT_ANSWERS } from "@/store/modules/candidates/constants";
import Vue from "vue";
import { required } from "vuelidate/lib/validators";
import CustomAudioPlayer from "@/components/CustomAudioPlayer.vue";
import { mapActions } from "vuex";

export default Vue.extend({
  name: "AppEngListeningQuestionsAssessment",
  props: {
    assessment_data: { type: Array, required: true },
    assessment_type: { type: String, required: false },
    restored_state: { type: Object, required: false, default: null }
  },
  components: {
    CustomAudioPlayer
  },
  data() {
    return {
      currentPart: 1,
      hasRestoredFromSave: false,
      answers: [],
      answersMap: {},
      submitLoading: false,
      submitError: "",
      bot_speaking: false,
      time_over: false
    };
  },
  computed: {
    /**
     * Groups questions by part and sorts them by order within each part
     * Returns array of part groups with audio, narration, and sorted questions
     */
    groupedParts() {
      if (
        !Array.isArray(this.assessment_data) ||
        this.assessment_data.length === 0
      ) {
        return [];
      }

      // Group questions by part
      const partsMap = {};
      this.assessment_data.forEach((question) => {
        if (!question || !question.question_options) return;

        const part = question.question_options.part;
        if (!part) return;

        if (!partsMap[part]) {
          partsMap[part] = {
            part: part,
            questions: [],
            audioUrl: null,
            narrationText: null
          };
        }

        partsMap[part].questions.push(question);

        // Extract audio URL and narration text from first question of each part
        if (!partsMap[part].audioUrl && question.question_options.statement) {
          partsMap[part].audioUrl = question.question_options.statement;
        }
        if (
          !partsMap[part].narrationText &&
          question.question_options.narration_text
        ) {
          partsMap[part].narrationText =
            question.question_options.narration_text;
        }
      });

      // Convert to array and sort by part number
      const partsArray = Object.values(partsMap).sort(
        (a, b) => a.part - b.part
      );

      // Sort questions within each part by order
      partsArray.forEach((partGroup) => {
        partGroup.questions.sort((a, b) => {
          const orderA = a.question_options?.order || 0;
          const orderB = b.question_options?.order || 0;
          return orderA - orderB;
        });
      });

      return partsArray;
    },
    /**
     * Get the current part group being displayed
     */
    currentPartGroup() {
      return this.groupedParts.find((p) => p.part === this.currentPart);
    },
    /**
     * Get total number of parts
     */
    totalParts() {
      return this.groupedParts.length;
    },
    /**
     * Check if current part is the last part
     */
    isLastPart() {
      if (this.groupedParts.length === 0) return false;
      const maxPart = Math.max(...this.groupedParts.map((p) => p.part), 0);
      return this.currentPart >= maxPart;
    },
    /**
     * Label for the Next button (always "Next" for listening questions)
     */
    nextButtonLabel() {
      if (this.$t) {
        const label = this.$t("shared.next");
        return label && label.toString ? label.toString() : "Next";
      }
      return "Next";
    },
    /**
     * Get all questions flattened for validation
     */
    allQuestions() {
      return this.groupedParts.flatMap((part) => part.questions);
    },
    /**
     * Get questions for current part only (for validation)
     */
    currentPartQuestions() {
      return this.currentPartGroup?.questions || [];
    },
    currentAssessmentQuestions() {
      // For backward compatibility, return current part questions
      return this.assessment_data?.filter(
        (a) =>
          a &&
          a.question_options &&
          a.question_options.part === this.currentPart
      );
    },
    isLastScenario() {
      return this.isLastPart;
    },
    currentQuestions() {
      const list =
        this.currentAssessmentQuestions[0]?.question_options?.questions;
      return Array.isArray(list) ? list : [];
    },
    canSubmit() {
      // Check if current part questions are all answered
      if (!this.currentPartQuestions || this.currentPartQuestions.length === 0)
        return false;
      const allAnswered = this.currentPartQuestions.every((q) => {
        const answer = this.getAnswerForQuestion(q.id);
        return answer && answer.trim() !== "";
      });
      return allAnswered && !this.$v.$invalid;
    },
    disabled_next_question() {
      return this.$v.$invalid || this.submitLoading;
    }
  },
  mounted() {
    // Restore from saved state if available
    if (this.restored_state) {
      this.restoreFromSavedState();
    } else {
      this.deriveCurrentIndexFromAnswers();
      this.prepareAnswersForCurrentScenario();
    }
  },
  watch: {
    restored_state: {
      handler(newVal) {
        if (newVal && (newVal.currentPart || newVal.answersMap)) {
          this.restoreFromSavedState();
        }
      },
      deep: true
    },
    assessment_data: {
      handler(newVal) {
        if (!Array.isArray(newVal) || newVal.length === 0) return;
        if (!this.restored_state && !this.hasRestoredFromSave) {
          this.deriveCurrentIndexFromAnswers();
          this.answersMap = {};
          this.prepareAnswersForCurrentScenario();
        }
      },
      deep: true
    },
    // Rebuild answers array when groupedParts changes
    groupedParts: {
      handler() {
        if (!this.restored_state && !this.hasRestoredFromSave) {
          this.deriveCurrentIndexFromAnswers();
          this.prepareAnswersForCurrentScenario();
        }
      },
      deep: true
    },
    // Update answers when current part changes
    currentPart() {
      this.prepareAnswersForCurrentScenario();
      this.emitStateChange();
    },
    // Emit state changes for persistence
    answersMap: {
      handler() {
        this.emitStateChange();
      },
      deep: true
    }
  },
  methods: {
    ...mapActions("candidate", {
      submit_assessment_answers: SUBMIT_APP_ASSESSMENT_ANSWERS
    }),
    event_cancellation(event) {
      event.preventDefault();
    },
    /**
     * Restore state from saved session
     */
    restoreFromSavedState() {
      if (!this.restored_state) return;

      this.hasRestoredFromSave = true;
      const incomingPart =
        this.restored_state.currentPart != null
          ? Number(this.restored_state.currentPart)
          : 1;
      // Never downgrade currentPart (e.g. Part 2 -> Part 1) when restore fires again
      this.currentPart = Math.max(this.currentPart, incomingPart);
      if (this.restored_state.answersMap) {
        this.answersMap = { ...this.restored_state.answersMap };
        if (this.answersMap[this.currentPart]) {
          this.answers = [...this.answersMap[this.currentPart]];
        }
      }

      this.prepareAnswersForCurrentScenario();
    },
    /**
     * Emit current state for persistence
     */
    emitStateChange() {
      this.$emit("state_changed", {
        section: "listening",
        state: {
          currentPart: this.currentPart,
          answersMap: this.answersMap,
          answers: this.answers
        }
      });
    },
    prepareAnswersForCurrentScenario() {
      // Initialize answers for current part if not already done
      const currentGroup = this.currentPartGroup;
      if (currentGroup && !this.answersMap[currentGroup.part]) {
        const partAnswers = currentGroup.questions.map((q) => ({
          question_id: q.id,
          question_answer: ""
        }));
        this.$set(this.answersMap, currentGroup.part, partAnswers);
      }

      // Create a flat answers array for validation (current part questions only)
      if (currentGroup) {
        this.answers = currentGroup.questions.map((q) => {
          const existingAnswer = this.answersMap[currentGroup.part]?.find(
            (ans) => ans.question_id === q.id
          );
          return (
            existingAnswer || {
              question_id: q.id,
              question_answer: ""
            }
          );
        });
      } else {
        this.answers = [];
      }

      this.$v && this.$v.$reset();
    },
    /**
     * Get answer for a specific question by question_id
     */
    getAnswerForQuestion(questionId) {
      // Search through all parts to find the answer
      for (const partNum in this.answersMap) {
        const answer = this.answersMap[partNum].find(
          (ans) => ans.question_id === questionId
        );
        if (answer) {
          return answer.question_answer;
        }
      }
      return "";
    },
    /**
     * Get error state for a specific question
     */
    getAnswerError(questionId) {
      const flatIndex = this.answers.findIndex(
        (ans) => ans.question_id === questionId
      );
      if (flatIndex === -1) return false;

      const vNode = this.$v?.answers?.$each?.[flatIndex]?.question_answer;
      return vNode && vNode.$error;
    },
    /**
     * Get error messages for a specific question
     */
    getAnswerErrorMessages(questionId) {
      const flatIndex = this.answers.findIndex(
        (ans) => ans.question_id === questionId
      );
      if (flatIndex === -1) return [];

      const vNode = this.$v?.answers?.$each?.[flatIndex]?.question_answer;
      if (!vNode || !vNode.$dirty) return [];
      const errs = [];
      if (!vNode.required) errs.push(this.$t("rules.required"));
      return errs;
    },
    /**
     * Get answer object for a question
     */
    getAnswerObject(questionId) {
      for (const partNum in this.answersMap) {
        const answer = this.answersMap[partNum].find(
          (ans) => ans.question_id === questionId
        );
        if (answer) return answer;
      }
      return null;
    },
    /**
     * Get the index of an answer in the answersMap for a specific part
     */
    getAnswerIndex(questionId) {
      const part = this.getQuestionPart(questionId);
      if (!part || !this.answersMap[part]) return -1;
      return this.answersMap[part].findIndex(
        (ans) => ans.question_id === questionId
      );
    },
    /**
     * Get the part number for a question
     */
    getQuestionPart(questionId) {
      const question = this.assessment_data?.find((q) => q.id === questionId);
      return question?.question_options?.part;
    },
    answerErrors(index) {
      const vNode = this.$v?.answers?.$each[index]?.question_answer;
      if (!vNode || !vNode.$dirty) return [];
      const errs = [];
      if (!vNode.required) errs.push(this.$t("rules.required"));
      return errs;
    },
    onAnswerInput(value, index) {
      if (this.answers[index]) {
        this.answers[index].question_answer = value;
      }
      if (this.$v.answers.$each[index]?.question_answer) {
        this.$v.answers.$each[index].question_answer.$touch();
      }
    },
    buildScenarioAnswerPayload() {
      // Never send done: true from Listening – only the final Submit (Speaking)
      // should mark the assessment as submitted.
      const questionPayloadArray = [];
      const currentGroup = this.currentPartGroup;

      if (currentGroup) {
        currentGroup.questions.forEach((question) => {
          const answerObj = this.answersMap[currentGroup.part]?.find(
            (ans) => ans.question_id === question.id
          );

          if (answerObj) {
            questionPayloadArray.push({
              assessment_id: question.assessment_id,
              question_id: question.id,
              answer_type: AppAssessments.QuestionType.JSON,
              answer: answerObj.question_answer || ""
            });
          }
        });
      }

      return questionPayloadArray;
    },
    deriveCurrentIndexFromAnswers() {
      if (
        !Array.isArray(this.assessment_data) ||
        !this.assessment_data.length
      ) {
        this.currentPart = 1;
        return;
      }
      // Find the first part number from grouped parts
      if (this.groupedParts.length > 0) {
        const minPart = Math.min(...this.groupedParts.map((p) => p.part));
        this.currentPart = minPart;
      } else {
        this.currentPart = 1;
      }
    },
    async send_user_response() {
      this.time_over = true;
      this.bot_speaking = true;
      await this.saveAllRemainingScenariosAsEmpty();
    },
    async saveAllRemainingScenariosAsEmpty() {
      // Ensure all parts have answers initialized (even if empty)
      this.groupedParts.forEach((partGroup) => {
        if (!this.answersMap[partGroup.part]) {
          this.answersMap[partGroup.part] = partGroup.questions.map((q) => ({
            question_id: q.id,
            question_answer: ""
          }));
        }
      });

      try {
        this.submitLoading = true;

        // Submit all answers for all parts
        for (const partGroup of this.groupedParts) {
          for (const question of partGroup.questions) {
            const answerObj = this.answersMap[partGroup.part]?.find(
              (ans) => ans.question_id === question.id
            );

            const payload = {
              assessment_id: question.assessment_id,
              question_id: question.id,
              answer_type: AppAssessments.QuestionType.JSON,
              answer: answerObj?.question_answer || ""
            };

            const response = await this.submit_assessment_answers(payload);
            if (!response)
              throw new Error("Failed to save part " + partGroup.part);
          }
        }

        this.$emit("assessment_completed", {
          answers: this.answersMap,
          total_scenarios: this.groupedParts.length,
          saved: true,
          time_expired: true
        });
      } catch (e) {
        this.submitError =
          (e && e.message) || this.$t("assessments.went-wrong").toString();
        this.bot_speaking = false;
      } finally {
        this.submitLoading = false;
      }
    },
    submitAnswers() {
      this.$v.$touch();
      if (this.$v.$invalid) return;

      // Ensure current part answers are saved
      const currentGroup = this.currentPartGroup;
      if (currentGroup && !this.answersMap[currentGroup.part]) {
        this.answersMap[currentGroup.part] = currentGroup.questions.map(
          (q) => ({
            question_id: q.id,
            question_answer: this.getAnswerForQuestion(q.id) || ""
          })
        );
      }

      this.persistScenario();
    },
    async persistScenario() {
      this.submitError = "";
      const isLast = this.isLastPart || this.time_over;
      const payloadArray = this.buildScenarioAnswerPayload();

      if (!payloadArray || !payloadArray.length) {
        this.submitError = this.$t("assessments.went-wrong").toString();
        return;
      }

      this.submitLoading = true;
      this.bot_speaking = true;

      try {
        for (const payload of payloadArray) {
          const response = await this.submit_assessment_answers(payload);
          if (!response) throw new Error("Empty response");
        }

        const currentGroup = this.currentPartGroup;
        // Emit completion for current part
        if (currentGroup) {
          this.$emit("scenario_completed", {
            index: currentGroup.part,
            assessment_id: currentGroup.questions[0]?.assessment_id,
            answers: this.answersMap[currentGroup.part] || [],
            saved: true
          });

          // Emit answered question ids for global progress tracking
          const ids = (currentGroup.questions || [])
            .map((q) => Number(q?.id))
            .filter((id) => Number.isFinite(id) && id > 0);
          if (ids.length > 0) {
            this.$emit("question_answered", ids);
          }
        }

        if (isLast) {
          // All parts completed
          this.$emit("assessment_completed", {
            answers: this.answersMap,
            total_scenarios: this.groupedParts.length,
            saved: true
          });
        } else {
          // Move to next part
          this.moveToNextPart();
          this.bot_speaking = false;
        }
      } catch (e) {
        this.submitError =
          (e && e.message) || this.$t("assessments.went-wrong").toString();
        this.bot_speaking = false;
      } finally {
        this.submitLoading = false;
      }
    },
    /**
     * Move to the next part
     */
    moveToNextPart() {
      const sortedParts = this.groupedParts
        .map((p) => p.part)
        .sort((a, b) => a - b);
      const currentIndex = sortedParts.indexOf(this.currentPart);
      if (currentIndex !== -1 && currentIndex < sortedParts.length - 1) {
        this.currentPart = sortedParts[currentIndex + 1];
        this.prepareAnswersForCurrentScenario();
      }
    }
  },
  validations() {
    return {
      answers: {
        $each: {
          question_answer: { required }
        }
      }
    };
  }
});
</script>

<style scoped>
.listening-assessment {
  padding: 20px;
}
.progress-bar {
  margin-bottom: 12px;
  font-weight: 600;
}
.progress-indicator {
  margin-bottom: 20px;
  padding: 10px 15px;
  background: #e3f2fd;
  border-radius: 6px;
  font-weight: 600;
  color: #1976d2;
  text-align: center;
}
.audio-box {
  background: #f7f7f7;
  padding: 15px;
  border-radius: 6px;
  margin-bottom: 25px;
}
.part-section {
  margin-bottom: 40px;
  padding-bottom: 30px;
  border-bottom: 2px solid #e0e0e0;
}

.part-section:last-child {
  border-bottom: none;
}

.part-title {
  font-size: 1.2em;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
}

.questions-box {
  margin-bottom: 16px;
}

.questions-title {
  font-size: 1.1em;
  font-weight: 600;
  color: #333;
  margin-bottom: 15px;
}

.question-item {
  margin-bottom: 20px;
  .required {
    color: red;
  }
}
</style>
